import { createContext, useContext, ReactNode } from "react";
import { usePersistedState } from "@/hooks/use-persisted-state";
import { YIELD_RATES } from "@/lib/sovereign";

const DEFAULT_YIELD = (YIELD_RATES.DAILY_APY * 100).toFixed(4);

interface Scenario {
  depositUSD: string;
  rewardYield: string;
  days: string;
}

interface LGNSInputsCtx {
  marketValue: string;
  setMarketValue: (v: string) => void;
  scenA: Scenario;
  setScenA: (patch: Partial<Scenario>) => void;
  scenB: Scenario;
  setScenB: (patch: Partial<Scenario>) => void;
  eatDeposit: string;
  setEatDeposit: (v: string) => void;
  eatRewardYield: string;
  setEatRewardYield: (v: string) => void;
  eatDays: string;
  setEatDays: (v: string) => void;
}

const Ctx = createContext<LGNSInputsCtx | null>(null);

function normalizeYield(val: string): string {
  return (!val || val.trim() === "" || isNaN(parseFloat(val))) ? DEFAULT_YIELD : val;
}

function normalizeScenario(s: Scenario): Scenario {
  return { ...s, rewardYield: normalizeYield(s.rewardYield) };
}

export function LGNSInputsProvider({ children }: { children: ReactNode }) {
  const [marketValue, setMarketValue] = usePersistedState("lgns-suite:mv", "");
  const [rawScenA, _setScenA] = usePersistedState<Scenario>("lgns-suite:scenA", { depositUSD: "", rewardYield: DEFAULT_YIELD, days: "" });
  const [rawScenB, _setScenB] = usePersistedState<Scenario>("lgns-suite:scenB", { depositUSD: "", rewardYield: DEFAULT_YIELD, days: "" });
  const [eatDeposit, setEatDeposit] = usePersistedState("lgns-suite:eat:deposit", "");
  const [rawEatRewardYield, setEatRewardYield] = usePersistedState("lgns-suite:eat:ry", DEFAULT_YIELD);
  const [eatDays, setEatDays] = usePersistedState("lgns-suite:eat:days", "");

  const scenA = normalizeScenario(rawScenA);
  const scenB = normalizeScenario(rawScenB);
  const eatRewardYield = normalizeYield(rawEatRewardYield);

  function setScenA(patch: Partial<Scenario>) {
    _setScenA(prev => ({ ...prev, ...patch }));
  }

  function setScenB(patch: Partial<Scenario>) {
    _setScenB(prev => ({ ...prev, ...patch }));
  }

  return (
    <Ctx.Provider value={{
      marketValue, setMarketValue,
      scenA, setScenA,
      scenB, setScenB,
      eatDeposit, setEatDeposit,
      eatRewardYield, setEatRewardYield,
      eatDays, setEatDays,
    }}>
      {children}
    </Ctx.Provider>
  );
}

export function useLGNSInputs() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useLGNSInputs must be inside LGNSInputsProvider");
  return ctx;
}
